import { useState, useCallback, useEffect } from "react";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { articles } from "@/data/articles";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Section images
import maestroNasri from "@/assets/maestro-nasri.jpg";
import maestroOh from "@/assets/maestro-oh.jpg";
import maestroRicci from "@/assets/maestro-ricci.jpg";
import maestroSilvio from "@/assets/maestro-silvio.jpg";
import maestroValerio from "@/assets/maestro-valerio.jpg";
import academyTeam from "@/assets/academy-team.jpg";
import agonistiImage from "@/assets/agonisti-upload-optimized.jpg";

interface GalleryImage {
  src: string;
  alt: string;
  category: string;
}

const categoryLabels: Record<string, string> = {
  bambini: "Bambini",
  ragazzi: "Ragazzi",
  adulti: "Adulti",
  generale: "Generale",
  zona: "Zona",
  agonisti: "Agonisti",
};

// Static section images
const sectionImages: GalleryImage[] = [
  { src: maestroNasri, alt: "Maestro Nasri", category: "Maestri" },
  { src: maestroOh, alt: "Maestro Oh", category: "Maestri" },
  { src: maestroRicci, alt: "Maestro Ricci", category: "Maestri" },
  { src: maestroSilvio, alt: "Maestro Silvio", category: "Maestri" },
  { src: maestroValerio, alt: "Maestro Valerio", category: "Maestri" },
  { src: academyTeam, alt: "Academy Team", category: "Academy" },
  { src: agonistiImage, alt: "Corso agonisti", category: "Agonisti" },
];

// Collect all gallery images from articles
const articleImages: GalleryImage[] = articles
  .filter((a) => a.gallery && a.gallery.length > 0)
  .flatMap((a) =>
    a.gallery!.map((src, i) => ({
      src,
      alt: `${a.title} - foto ${i + 1}`,
      category: categoryLabels[a.category] || a.category,
    }))
  );

const allImages: GalleryImage[] = [...articleImages, ...sectionImages];

// Get unique categories
const allCategories = ["Tutte", ...Array.from(new Set(allImages.map((img) => img.category)))];

export const FullGallery = () => {
  const [activeFilter, setActiveFilter] = useState("Tutte");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const filteredImages =
    activeFilter === "Tutte"
      ? allImages
      : allImages.filter((img) => img.category === activeFilter);

  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);

  const goNext = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex + 1) % filteredImages.length);
  }, [lightboxIndex, filteredImages.length]);

  const goPrev = useCallback(() => {
    if (lightboxIndex === null) return;
    setLightboxIndex((lightboxIndex - 1 + filteredImages.length) % filteredImages.length);
  }, [lightboxIndex, filteredImages.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [lightboxIndex, goNext, goPrev]);

  return (
    <section className="section-padding bg-secondary">
      <div className="container-wide mx-auto">
        <SectionHeader
          title="Tutte le foto"
          subtitle="Una raccolta di tutti i momenti catturati nella nostra palestra"
        />

        {/* Category filter */}
        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {allCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeFilter === cat
                  ? "bg-gold text-black-deep"
                  : "bg-background text-muted-foreground hover:bg-gold/10 hover:text-gold"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredImages.map((item, index) => (
            <div
              key={`${item.category}-${index}`}
              className="group relative aspect-[4/3] overflow-hidden rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 cursor-pointer"
              onClick={() => openLightbox(index)}
            >
              <img
                src={item.src}
                alt={item.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 hero-overlay-soft pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-black-deep/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="font-body text-xs font-medium text-gold/80 uppercase tracking-wider">
                    {item.category}
                  </span>
                  <h3 className="font-display text-sm font-semibold text-gold mt-1 line-clamp-2">
                    {item.alt}
                  </h3>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-8">
          {filteredImages.length} foto
          {activeFilter !== "Tutte" && ` in "${activeFilter}"`}
        </p>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/70 hover:text-white z-10"
            aria-label="Chiudi"
          >
            <X className="h-8 w-8" />
          </button>

          {/* Prev */}
          <button
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-10"
            aria-label="Foto precedente"
          >
            <ChevronLeft className="h-10 w-10" />
          </button>

          {/* Image */}
          <img
            src={filteredImages[lightboxIndex].src}
            alt={filteredImages[lightboxIndex].alt}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next */}
          <button
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-10"
            aria-label="Foto successiva"
          >
            <ChevronRight className="h-10 w-10" />
          </button>

          {/* Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/60 px-2 py-1 rounded">
            {lightboxIndex + 1} / {filteredImages.length}
          </div>
        </div>
      )}
    </section>
  );
};
